import { Text, Box, HStack, VStack, Flex, Badge, Icon } from "@chakra-ui/react";
import { useEffect } from "react";
import { FaCalendarAlt, FaBriefcase } from "react-icons/fa";

import { documentFormatter } from "@/utils/documentFormatter";
import { format } from "date-fns";
import { BalanceSolicitationAnalysisResponse } from "@/models/consignado/balanceSolicitation.model";


interface MarginEmployerSelectProps {
  analysisData: BalanceSolicitationAnalysisResponse;
  setSelectedVinculoId: (vinculoId: string) => void;
  selectedVinculoId: string | null;
}

export function MarginEmployerSelect({
  analysisData,
  setSelectedVinculoId,
  selectedVinculoId,
}: MarginEmployerSelectProps) {
  useEffect(() => {
    if (analysisData?.vinculos_empregaticios?.length) {
      setSelectedVinculoId(analysisData.vinculos_empregaticios[0].id);
    }
  }, [analysisData]);

  const formatDate = (date: Date | string | null) => {
    if (!date) return "Não informado";
    try {
      const dateObj = typeof date === "string" ? new Date(date) : date;
      return format(dateObj, "dd/MM/yyyy");
    } catch {
      return "Data inválida";
    }
  };

  return (
    <Box
      bg="white"
      borderRadius="lg"
      p={{ base: 2, md: 3 }}
      boxShadow="sm"
      border="1px solid"
      borderColor="gray.200"
      flex={{ base: "none", lg: "0 0 300px" }}
      minW={{ base: "100%", lg: "300px" }}
      maxW={{ base: "100%", lg: "400px" }}
      display="flex"
      flexDirection="column"
      overflow="hidden"
      minH={0}
    >
      <HStack spacing={2} mb={3} flexShrink={0}>
        <Icon as={FaBriefcase} color="secondary" boxSize={{ base: 3, md: 4 }} />
        <Text
          fontSize={{ base: "sm", md: "md" }}
          fontWeight="bold"
          color="gray.700"
        >
          Vínculos Empregatícios
        </Text>
      </HStack>
      <Text
        fontSize={{ base: "2xs", md: "xs" }}
        color="gray.500"
        mb={3}
        flexShrink={0}
      >
        Selecione um vínculo para ver os relatórios
      </Text>

      <Box
        flex={1}
        overflowY="auto"
        minH={0}
        css={{
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <VStack align="stretch" spacing={2}>
          {analysisData.vinculos_empregaticios?.map((vinculo) => {
            const isSelected = vinculo.id === selectedVinculoId;
            const reportsCount = vinculo.relatorios?.length || 0;

            return (
              <Box
                key={vinculo.id}
                p={{ base: 2, md: 3 }}
                borderRadius="md"
                border="2px solid"
                borderColor={isSelected ? "secondary" : "gray.200"}
                bg={isSelected ? "purple.50" : "white"}
                cursor="pointer"
                onClick={() => setSelectedVinculoId(vinculo.id)}
                _hover={{
                  borderColor: "secondary",
                  bg: "purple.50",
                }}
                transition="all 0.2s"
              >
                <Flex justify="space-between" align="start" mb={1} gap={2}>
                  <Text
                    fontSize={{ base: "xs", md: "sm" }}
                    fontWeight="semibold"
                    color="gray.700"
                    flex={1}
                    isTruncated
                  >
                    {vinculo.empregador.nome || "Empregador não informado"}
                  </Text>
                  <Badge
                    colorScheme="blue"
                    borderRadius="full"
                    px={{ base: 1.5, md: 2 }}
                    py={0.5}
                    fontSize={{ base: "2xs", md: "xs" }}
                    flexShrink={0}
                  >
                    {reportsCount}
                  </Badge>
                </Flex>

                <VStack align="stretch" spacing={0.5} mt={1}>
                  {vinculo.empregador.cnpj && (
                    <Text
                      fontSize={{ base: "2xs", md: "xs" }}
                      color="gray.600"
                      isTruncated
                    >
                      <Text as="span" fontWeight="medium">
                        CNPJ:
                      </Text>{" "}
                      {documentFormatter(vinculo.empregador.cnpj)}
                    </Text>
                  )}
                  {vinculo.data_admissao && (
                    <HStack spacing={1}>
                      <Icon
                        as={FaCalendarAlt}
                        color="gray.500"
                        boxSize={{ base: 2, md: 2.5 }}
                        flexShrink={0}
                      />
                      <Text
                        fontSize={{ base: "2xs", md: "xs" }}
                        color="gray.600"
                        isTruncated
                      >
                        <Text as="span" fontWeight="medium">
                          ADMISSÃO:
                        </Text>{" "}
                        {formatDate(vinculo.data_admissao)}
                      </Text>
                    </HStack>
                  )}
                  {vinculo.descricao_cbo && (
                    <Text
                      fontSize={{ base: "2xs", md: "xs" }}
                      color="gray.600"
                      isTruncated
                    >
                      {vinculo.descricao_cbo}
                    </Text>
                  )}
                </VStack>
              </Box>
            );
          })}
        </VStack>
      </Box>
    </Box>
  );
}
